import i18n from "@/i18n";

export type ApiResponse<Type> =
  | {
      success: true;
      result: Type;
    }
  | {
      success: false;
      message: string;
      result: undefined;
    };

export interface AuthResponse {
  accessToken: string;
}

export interface CustomRequestInit extends RequestInit {
  headers?: HeadersInit;
  /** Retry 5xx and network errors even for POST/PATCH. Only for requests that are safe to repeat. */
  retry?: boolean;
}

export interface FetchWithAuthOptions extends CustomRequestInit {
  requestContext?: string; // e.g., 'AUTH', 'USER_SETTINGS', etc.
  errorHandlers?: {
    [statusCode: number]: (response: Response, responseBody: any) => void;
  };
}

export class ApiError extends Error {
  constructor(public message: string) {
    super(message);
  }

  toString(): string {
    return this.message;
  }
}

export interface BackendErrorResponse {
  error: string;
  message: string;
  timestamp: string; // ISO 8601 string from Instant
}

// User-facing text comes only from locales: a known error code, else the HTTP status. Backend `message` is never shown.
export const getErrorMessage = (status: number = 500, errorCode?: string, authType: string = "default"): string => {
  const messageConfig = errorCode ? errorMessages[errorCode.toLowerCase()] : undefined;
  const translationKey = messageConfig?.[authType] || messageConfig?.default;

  if (translationKey) {
    return i18n.t(translationKey);
  }

  // Auth flows (login, social login) report a rejected attempt the same way regardless of status.
  if (authType !== "default" && (status === 401 || status === 403)) {
    return i18n.t("login.loginError");
  }

  return i18n.t(statusMessageKeys[status] ?? "common.error.500");
};

export const getNetworkErrorMessage = (): string => i18n.t("common.error.network");

const errorMessages: Record<string, Record<string, string>> = {
  private_relay_not_allowed: {
    apple: "login.error.privateRelayNotAllowed",
  },
  social_user_not_registered: {
    default: "login.error.socialUserNotRegistered",
  },
  user_not_found: {
    default: "login.error.userNotFound",
  },
  invalid_credentials: {
    default: "login.loginError",
  },
  token_invalid: {
    default: "login.loginError",
  },
  token_expired: {
    default: "login.error.tokenExpired",
  },
};

const statusMessageKeys: Record<number, string> = {
  400: "common.error.400",
  401: "common.error.401",
  403: "common.error.403",
  404: "common.error.404",
  409: "common.error.409",
  413: "common.error.413",
};
